<template>
  <div>
    <section :class="$style.sectionRoot">
      <h2
        :class="$style.sectionHeader"
        data-test-id="CreateSystem__sectionHeader"
      >
        {{ title }} Install
        <a
          v-if="showModalLink"
          :class="$style.modalLink"
          href="#"
          @click.prevent="showModal"
        >
          watch: how to install on {{ title }}
        </a>
      </h2>
      <div :class="$style.sectionContent">
        <slot name="instructions" />
        <div v-if="tabs.length">
          <TabList
            ref="tabsList"
            :activeTab="activeTab"
            :class="$style.tabContainer"
            :customActiveTabStyle="$style.activeTabHeader"
            :customNavStyle="$style.navList"
            :customTabStyle="$style.tabHeader"
            :tabs="tabs"
            @active-tab-change="toggleActiveTab"
          />
        </div>
        <div v-else>
          <CopyWell
            v-if="copyWell"
            :content="copyWell.content"
            data-test-id="CreateSystem__CopyWell"
            :label="copyWell.label"
            :location="`Create System - ${copyWell.label}`"
            :rows="copyWell.rows"
            :showTooltip="copyWell.showTooltip"
            :tooltipLabel="copyWell.tooltipLabel"
            @copyToClipboard="logSystemAdded"
          />
          <CompatibilityNotes
            v-if="compatibilityNotes"
            :compatibilityNotes="compatibilityNotes"
          />
          <div
            v-if="showInstallerNotes"
            :class="$style.installerNotes"
          >
            <InstallerNotes />
            <p :class="$style.subtleText">
              Note that this process may take between 10 and 120 seconds,
              depending on environmental
              conditions. Once the agent has been successfully installed,
              it will begin heartbeating to
              JumpCloud and will be immediately visible in your Systems inventory.
            </p>
            <p :class="$style.subtleText">
              If you have any problems with the install process please click on
              the Support button for help.
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { eventLocations, eventProperties, onboardingTasks } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import CompatibilityNotes from 'client/systems/CompatibilityNotes.vue';
import Constants, { OnboardingChecklistConsts } from 'util/Constants';
import CopyWell from 'client/components/FormValidation/CopyWell.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventManager from 'client/GrowthEngineering/EventManager';
import InstallerNotes from 'client/systems/InstallerNotes.vue';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import TabList from 'components/TabList/TabList.vue';
import VideoModal from 'client/components/Modal/VideoModal.vue';

const {
  description,
  location,
  text,
} = eventProperties;

const { linux, mac, windows } = Constants;
const {
  wistiaLinuxSystemSetupVideo,
  wistiaMacSystemSetupVideo,
  wistiaWindowsSystemSetupVideo,
} = OnboardingChecklistConsts;
const {
  triggerOnboardingChecklistModalAddSystemsInBulkLinkClicked,
  triggerSystemSetupModalDismissed,
} = EventManager;
const onboardingModule = 'OnboardingModule';

/**
 * This component will be rendered by OS specific parent components, e.g. CreateLinuxSystem, with
 * the appropriate parameters, to render an OS specific Create System component.
 */
export default {
  name: 'CreateSystem',

  components: {
    CompatibilityNotes,
    CopyWell,
    InstallerNotes,
    TabList,
  },

  props: {
    compatibilityNotes: {
      type: Object,
      default() {
        return null;
      },
      validator(value) {
        if (!value) {
          return true;
        }

        return !!(value.header && value.text);
      },
    },
    copyWell: {
      type: Object,
      default() {
        return null;
      },
      validator(value) {
        if (!value) {
          return true;
        }

        return !!(value.label
                  && value.content
                  && (value.rows ? typeof value.rows === 'number' : true));
      },
    },
    showInstallerNotes: {
      type: Boolean,
      default: false,
    },
    tabs: {
      default: () => [],
      type: Array,
    },
    title: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    activeTab: 0,
  }),

  computed: {
    ...mapGetters('SettingsModule', {
      isCommandRunner: 'isCommandRunner',
    }),

    osType() {
      let osType;
      if (this.title === 'Mac') {
        osType = mac;
      } else if (this.title === 'Windows') {
        osType = windows;
      } else if (this.title === 'Linux') {
        osType = linux;
      }
      return osType;
    },

    showModalLink() {
      return this.title === 'Linux' || this.title === 'Windows';
    },
  },

  methods: {
    ...mapActions(onboardingModule, [
      'completeOnboardingTask',
    ]),

    logSystemAdded() {
      const { triggerSystemAdded } = EventManager;
      triggerSystemAdded(this.title.toLowerCase());
      this.completeOnboardingTask(onboardingTasks.discoverResources);
      this.completeOnboardingTask(onboardingTasks.systemCreated);
    },
    showModal() {
      EventHelper.triggerModalViewed({
        [description]: 'Watch a tutorial video',
        [location]: eventLocations.devicesPage,
        [text]: `watch: how to install on ${this.title}`,
      });
      const kbLinkObject = {
        action: () => triggerOnboardingChecklistModalAddSystemsInBulkLinkClicked(),
        url: 'https://support.jumpcloud.com/support/s/article/agent-deployment-via-command-line1',
        text: 'Add systems in bulk',
      };
      ModalEventBus.open({
        component: VideoModal,
        isLargeModal: true,
        componentProps: {
          header: 'Set up a System',
          modalClosedAction: () => triggerSystemSetupModalDismissed(),
          openAtTab: this.osType,
          videos: [
            {
              tabName: windows,
              iframeUrl: wistiaWindowsSystemSetupVideo,
              videoUrl: wistiaWindowsSystemSetupVideo,
              kbLinkObject,
            },
            {
              tabName: mac,
              iframeUrl: wistiaMacSystemSetupVideo,
              videoUrl: wistiaMacSystemSetupVideo,
              kbLinkObject,
            },
            {
              tabName: linux,
              iframeUrl: wistiaLinuxSystemSetupVideo,
              videoUrl: wistiaLinuxSystemSetupVideo,
              kbLinkObject,
            },
          ],
        },
      });
    },
    toggleActiveTab(index) {
      this.activeTab = index;
    },

    triggerKbLinkEvent() {
      const payload = { osType: this.osType };
      EventHelper.triggerSystemCompatibilityLearnMoreClicked(payload);
    },
  },
};
</script>
<style module>
@import '../css/sections.css';
@import '../components/FormValidation/css/formFields.css';

@value (
  iconGrey,
  gradientLight3,
  gradientDark4,
  white) from '~css/colors.css';

/*
 * TODO: Should we reuse the .jc-icon-inline style here for the margin-right and vertical-align
 * properties? Consider moving that to /client/css/icons.css when the G Suite/Microsoft 365 or
 * properties? Consider moving that to /client/css/icons.css when the Google/Microsoft 365 or
 * Workday asides are refactored. These views currently use this legacy rule-set from
 * /scss/components/general.scss.
 */

.kbArticle {
  display: inline-block;
  fill: iconGrey;
  height: 14px;
  margin-right: 4px;
  vertical-align: middle;
  width: 14px;
}

.installerNotes {
  border-top: 1px solid #eee;
  margin-top: 20px;
  padding-top: 20px;
}

.subtleText {
  color: iconGrey;
}

.modalLink {
  cursor: pointer;
  float: right;
  font-size: 1rem;
  font-weight: 500;
}

.navList {
  font-weight: 600;
  padding-bottom: 20px;
}

.tabHeader {
  font-size: 13px;
  margin: 0;
}

.navList li {
  background-color: gradientLight3;
  border: 1px solid var(--jcGrey);
  margin: 0;
  padding: 5px 20px;
}

.navList li:first-child {
  border-radius: 2px 0 0 2px;
}

.navList li:last-child {
  border-radius: 0 2px 2px 0;
}

.navList li.activeTabHeader {
  background-color: var(--jcPrimary);
}

.installHeader {
  margin-top: 10px;
}
</style>
